import { useQuery, QueryFunction } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { AdminUsersResponse, AccessLevel } from './types'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface UseAdminUsersParams {
  accessLevel?: AccessLevel
  first?: number
  after?: string
}

export const useAdminUsers = ({
  accessLevel = AccessLevel.Admin,
  first = 25,
  after,
}: UseAdminUsersParams = {}) => {
  const fetchWithAuth = useFetchWithAuth()

  const getAdminUsers: QueryFunction<AdminUsersResponse> = async () => {
    const params = new URLSearchParams({
      accessLevel,
      first: first.toString(),
    })

    if (after) {
      params.append('after', after)
    }

    const response = await fetchWithAuth(`${URL}/roles/admins?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    return response
  }

  return useQuery({
    queryKey: ['adminUsers', accessLevel, first, after] as const,
    queryFn: getAdminUsers,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Lazy query version for compatibility with existing code
export const useAdminUsersLazy = () => {
  const fetchWithAuth = useFetchWithAuth()

  const executeQuery = async (variables: {
    accessLevel: AccessLevel
    first: number
    after?: string
  }) => {
    const params = new URLSearchParams({
      accessLevel: variables.accessLevel,
      first: variables.first.toString(),
    })

    if (variables.after) {
      params.append('after', variables.after)
    }

    const response = await fetchWithAuth(`${URL}/roles/admins?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    return response
  }

  return [executeQuery]
}
